import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AlarmVoyager, NotificationAlertZone } from '@/types/api';
import { useQueryAllNotificationAlert } from '@/api/queries/notification';
import ShipLoading from '@/components/ui/loader/ShipLoading';
import { BellRing, Calendar, MapPin, Route, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: (string | undefined | null | false)[]) {
  return twMerge(clsx(inputs));
}

type NotifactionZoneTableProps = {
  alias?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
};

export default function NotifactionTable({ alias, orderBy, orderType }: NotifactionZoneTableProps) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { data, isError, isLoading, isFetching } = useQueryAllNotificationAlert(
    alias,
    orderBy,
    orderType,
    page,
  );

  // @ts-ignore
  const notifications = data?.data || [];
  // @ts-ignore
  const totalRows = data?.meta?.total_record || 0;
  const totalPages = Math.ceil(totalRows / perPage) || 1;

  useEffect(() => {
    if (isError) {
      toast.error('Terjadi kesalahan saat mengambil notifikasi.');
    }
  }, [isError]);

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const isVoyager = alias === 'AV';

  return (
    <div className="bg-white dark:bg-gray-900 border-0 shadow dark:shadow-gray-800 rounded p-3 relative min-h-[400px]">
      {(isLoading || isFetching) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-900/50 backdrop-blur-[2px] rounded">
          <ShipLoading />
        </div>
      )}

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto -mx-3 px-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
              <th className="py-3 pr-3 font-semibold whitespace-nowrap w-2/5">Message</th>
              {!isVoyager && (
                <>
                  <th className="py-3 pr-3 font-semibold whitespace-nowrap">Alert Type</th>
                  <th className="py-3 pr-3 font-semibold whitespace-nowrap">Zone</th>
                </>
              )}
              <th className="py-3 pr-3 font-semibold whitespace-nowrap text-right">Created At</th>
            </tr>
          </thead>
          <tbody>
            {notifications.length === 0 && !(isLoading || isFetching) ? (
              <tr>
                <td colSpan={isVoyager ? 2 : 4} className="py-10 text-center text-gray-500 dark:text-gray-400">
                  No notifications found.
                </td>
              </tr>
            ) : (
              notifications.map((notif: any, i: number) => (
                <tr
                  key={i}
                  className="border-b border-gray-100 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="py-3 pr-3 max-w-sm">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 bg-blue-50 dark:bg-blue-900/20 p-1.5 rounded-lg text-blue-600 dark:text-blue-400 shrink-0">
                        {isVoyager ? <Route className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 font-medium">{notif.message}</p>
                    </div>
                  </td>
                  {!isVoyager && (
                    <>
                      <td className="py-3 pr-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800 uppercase tracking-wider gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {notif.notification_name || notif.alert_type}
                        </span>
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                          <MapPin className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate max-w-[200px]">{notif.zone_name || '-'}</span>
                        </div>
                      </td>
                    </>
                  )}
                  <td className="py-3 pr-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5 text-gray-500 dark:text-gray-400">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{notif.created_at}</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3 mt-2">
        {notifications.length === 0 && !(isLoading || isFetching) ? (
          <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            No notifications found.
          </div>
        ) : (
          notifications.map((notif: any, i: number) => (
            <div
              key={i}
              className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 shadow-sm"
            >
              <div className="flex gap-3 items-start">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg text-blue-600 dark:text-blue-400 shrink-0">
                  {isVoyager ? <Route className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{notif.message}</p>
                  
                  {!isVoyager && (notif.zone_name || notif.notification_name || notif.alert_type) && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2 border border-gray-100 dark:border-gray-800/50">
                        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                          Alert Type
                        </div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                          {notif.notification_name || notif.alert_type}
                        </div>
                      </div>
                      <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2 border border-gray-100 dark:border-gray-800/50">
                        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                          <MapPin className="w-3 h-3" /> Zone
                        </div>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 truncate">
                          {notif.zone_name || '-'}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 p-2 rounded border border-gray-100 dark:border-gray-800/50 w-fit">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{notif.created_at}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Custom Pagination */}
      {totalRows > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center sm:text-left">
            Showing <span className="font-medium text-gray-900 dark:text-white">{(page - 1) * perPage + 1}</span> to{' '}
            <span className="font-medium text-gray-900 dark:text-white">{Math.min(page * perPage, totalRows)}</span> of{' '}
            <span className="font-medium text-gray-900 dark:text-white">{totalRows}</span> entries
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className={cn(
                "p-1.5 rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                page === 1 && "opacity-50 cursor-not-allowed"
              )}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-medium px-2 dark:text-gray-300">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className={cn(
                "p-1.5 rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                page === totalPages && "opacity-50 cursor-not-allowed"
              )}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
